/**
 * Sector Map — Effort-Weighted Backlog Visualization
 *
 * Renders the item backlog as a proportional pie/sector chart where each
 * slice's arc is sized by total weighted effort units (altitude × scale
 * multiplier), not raw item count.
 *
 * The "1 unit" baseline = one L1 Tactical item — the smallest recognised work
 * increment. Everything else is expressed as a multiple of that baseline.
 */

import { useMemo, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Map, Info, ChevronDown, Layers } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import {
  ALTITUDE_LEVEL_NAMES,
  ALTITUDE_LEVELS_ORDERED,
  type AltitudeLevel,
} from '../../../types/prioritization/itemLifecycle';
import { useTheme } from '../../../contexts/ThemeContext';

// ============================================================================
// Types & constants
// ============================================================================

type ScaleId = 'linear' | 'fibonacci' | 'power2' | 'custom';

interface WeightScale {
  id: ScaleId;
  label: string;
  description: string;
  weights: Record<AltitudeLevel, number>;
}

const BUILT_IN_SCALES: WeightScale[] = [
  {
    id: 'linear',
    label: 'Linear',
    description: 'Each level adds one unit over the previous (1 / 2 / 3 / 4 / 5)',
    weights: { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 },
  },
  {
    id: 'fibonacci',
    label: 'Fibonacci',
    description: 'Fibonacci sequence — reflects real-world exponential growth (1 / 2 / 3 / 5 / 8)',
    weights: { 1: 1, 2: 2, 3: 3, 4: 5, 5: 8 },
  },
  {
    id: 'power2',
    label: 'Powers of 2',
    description: 'Each level doubles the previous — strong signal for sizing extremes (1 / 2 / 4 / 8 / 16)',
    weights: { 1: 1, 2: 2, 3: 4, 4: 8, 5: 16 },
  },
  {
    id: 'custom',
    label: 'Custom',
    description: 'Set your own weight per altitude level',
    weights: { 1: 1, 2: 3, 3: 5, 4: 8, 5: 13 },
  },
];

// Altitude 5 = Strategic (highest effort), 1 = Tactical (baseline unit)
const ALTITUDE_COLORS: Record<AltitudeLevel, string> = {
  5: '#6366f1', // indigo  — Strategic
  4: '#8b5cf6', // violet  — Program
  3: '#06b6d4', // cyan    — Capability
  2: '#10b981', // emerald — Delivery
  1: '#f59e0b', // amber   — Tactical (baseline)
};

// ============================================================================
// Tooltip
// ============================================================================

function SectorTooltip({
  active,
  payload,
  unitLabel,
}: {
  active?: boolean;
  payload?: any[];
  unitLabel: string;
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload as SliceData;
  const pct = d.pct;
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] px-3.5 py-2.5 shadow-lg text-sm">
      <div className="font-semibold text-[var(--text-pri)] mb-1">
        {d.name}
        <span className="ml-2 text-xs font-normal text-[var(--text-ter)]">L{d.level}</span>
      </div>
      <div className="space-y-0.5 text-xs text-[var(--text-sec)]">
        <div className="flex justify-between gap-8">
          <span>Items</span>
          <span className="font-medium text-[var(--text-pri)]">{d.count}</span>
        </div>
        <div className="flex justify-between gap-8">
          <span>Weight per item</span>
          <span className="font-medium text-[var(--text-pri)]">
            ×{d.weight} {unitLabel}{d.weight !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex justify-between gap-8">
          <span>Total {unitLabel}s</span>
          <span className="font-semibold text-[var(--text-pri)]">{d.totalUnits}</span>
        </div>
        <div className="mt-1 pt-1 border-t border-[var(--rule)] flex justify-between gap-8">
          <span>Share of effort</span>
          <span className="font-semibold" style={{ color: ALTITUDE_COLORS[d.level as AltitudeLevel] }}>
            {pct.toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Types
// ============================================================================

interface SliceData {
  level: number;
  name: string;
  count: number;
  weight: number;
  totalUnits: number;
  pct: number;
  color: string;
}

// ============================================================================
// Main component
// ============================================================================

export default function SectorMap() {
  const allItems = useItemsStore((s) => s.items);
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const { isDark } = useTheme();

  const [activeScale, setActiveScale] = useState<ScaleId>('fibonacci');
  const [customWeights, setCustomWeights] = useState<Record<AltitudeLevel, number>>(
    BUILT_IN_SCALES.find((s) => s.id === 'custom')!.weights,
  );
  const [unitLabel, setUnitLabel] = useState('unit');
  const [includeUnclassified, setIncludeUnclassified] = useState(false);
  const [activeSlice, setActiveSlice] = useState<number | null>(null);
  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(null);

  // Filter items by selected pool (or use all items)
  const items = useMemo(() => {
    if (!selectedPoolId) return allItems;
    const pool = itemPools.find((p) => p.id === selectedPoolId);
    if (!pool) return allItems;
    const poolItemIds = new Set(pool.priorityIds);
    return allItems.filter((item) => poolItemIds.has(item.id));
  }, [allItems, itemPools, selectedPoolId]);

  const selectedPool = selectedPoolId ? itemPools.find((p) => p.id === selectedPoolId) : null;

  const currentWeights: Record<AltitudeLevel, number> = useMemo(() => {
    if (activeScale === 'custom') return customWeights;
    return BUILT_IN_SCALES.find((s) => s.id === activeScale)!.weights;
  }, [activeScale, customWeights]);

  // Group items by altitude
  const { slices, totalUnits, unclassifiedCount } = useMemo(() => {
    const counts: Record<number, number> = {};
    let unclassified = 0;

    for (const item of items) {
      const lvl = item.altitudeLevel;
      if (!lvl) { unclassified++; continue; }
      counts[lvl] = (counts[lvl] || 0) + 1;
    }

    const out: SliceData[] = [];
    let total = 0;

    // Calculate total first (for pct)
    for (const lvl of ALTITUDE_LEVELS_ORDERED) {
      const count = counts[lvl] || 0;
      const weight = currentWeights[lvl] ?? 1;
      total += count * weight;
    }

    if (includeUnclassified && unclassified > 0) {
      total += unclassified * 1; // unclassified = 1 unit each (Tactical baseline)
    }

    for (const lvl of ALTITUDE_LEVELS_ORDERED) {
      const count = counts[lvl] || 0;
      if (count === 0) continue;
      const weight = currentWeights[lvl] ?? 1;
      const units = count * weight;
      out.push({
        level: lvl,
        name: ALTITUDE_LEVEL_NAMES[lvl],
        count,
        weight,
        totalUnits: units,
        pct: total > 0 ? (units / total) * 100 : 0,
        color: ALTITUDE_COLORS[lvl],
      });
    }

    if (includeUnclassified && unclassified > 0) {
      out.push({
        level: 0,
        name: 'Unclassified',
        count: unclassified,
        weight: 1,
        totalUnits: unclassified,
        pct: total > 0 ? (unclassified / total) * 100 : 0,
        color: '#94a3b8',
      });
    }

    return { slices: out, totalUnits: total, unclassifiedCount: unclassified };
  }, [items, currentWeights, includeUnclassified]);

  const currentScaleObj = BUILT_IN_SCALES.find((s) => s.id === activeScale)!;

  const surfaceBg = isDark ? 'var(--bg-raised)' : '#fff';
  const borderColor = isDark ? 'var(--rule-bright)' : '#e5e7eb';
  const mutedText = isDark ? '#94a3b8' : '#6b7280';
  const headingText = isDark ? '#e2e8f0' : '#111827';

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Map className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Sector Map"
        subtitle="Effort-weighted backlog — each sector is proportional to total work units, not item count"
      />

      {/* ── Pool selector ─────────────────────────────────────────────────── */}
      <div
        className="border p-4"
        style={{ background: surfaceBg, borderColor }}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <Layers className="h-4 w-4 flex-shrink-0" style={{ color: mutedText }} />
          <span className="text-sm font-medium" style={{ color: headingText }}>
            Item Pool
          </span>
          <div className="relative flex-1 min-w-48 max-w-xs">
            <select
              value={selectedPoolId ?? ''}
              onChange={(e) => setSelectedPoolId(e.target.value || null)}
              className="w-full appearance-none border pl-3 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-[var(--slate)]"
              style={{
                background: isDark ? 'var(--surface)' : '#fff',
                borderColor: isDark ? 'var(--rule-bright)' : '#e5e7eb',
                color: headingText,
              }}
            >
              <option value="">All items ({allItems.length})</option>
              {itemPools.map((pool) => (
                <option key={pool.id} value={pool.id}>
                  {pool.name} ({pool.priorityIds.length} items)
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5"
              style={{ color: mutedText }}
            />
          </div>
          {selectedPool && (
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                style={{
                  background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
                  color: isDark ? '#a5b4fc' : '#4338ca',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: '#6366f1' }}
                />
                {selectedPool.priorityIds.length} items in pool · {items.length} matched
              </span>
              <button
                onClick={() => setSelectedPoolId(null)}
                className="text-xs underline hover:no-underline"
                style={{ color: mutedText }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Scale picker & options ─────────────────────────────────────────── */}
      <div
        className="border p-5 space-y-4"
        style={{ background: surfaceBg, borderColor }}
      >
        <div className="flex flex-wrap items-start gap-6">
          {/* Scale selector */}
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-semibold uppercase tracking-widest mb-2"
              style={{ color: mutedText }}>
              Weight Scale
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {BUILT_IN_SCALES.map((scale) => (
                <button
                  key={scale.id}
                  onClick={() => setActiveScale(scale.id)}
                  className="px-3 py-1.5 text-sm font-medium transition-all duration-150 border"
                  style={
                    activeScale === scale.id
                      ? {
                          background: '#4f46e5',
                          color: '#fff',
                          borderColor: '#4f46e5',
                        }
                      : {
                          background: isDark ? 'var(--surface)' : '#f9fafb',
                          color: isDark ? '#94a3b8' : '#374151',
                          borderColor: isDark ? 'var(--rule-bright)' : '#e5e7eb',
                        }
                  }
                >
                  {scale.label}
                </button>
              ))}
            </div>
            {currentScaleObj && (
              <p className="mt-1.5 text-xs" style={{ color: mutedText }}>
                {currentScaleObj.description}
              </p>
            )}
          </div>

          {/* Unit label */}
          <div className="w-44">
            <label className="block text-xs font-semibold uppercase tracking-widest mb-2"
              style={{ color: mutedText }}>
              Unit Name
            </label>
            <input
              type="text"
              value={unitLabel}
              onChange={(e) => setUnitLabel(e.target.value || 'unit')}
              placeholder="unit"
              className="w-full border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-[var(--slate)]"
              style={{
                background: isDark ? 'var(--surface)' : '#fff',
                borderColor: isDark ? 'var(--rule-bright)' : '#e5e7eb',
                color: headingText,
              }}
            />
            <p className="mt-1 text-xs" style={{ color: mutedText }}>
              1 {unitLabel} = one L1 Tactical item
            </p>
          </div>

          {/* Include unclassified toggle */}
          <div className="pt-5">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <div
                className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200"
                style={{ background: includeUnclassified ? '#4f46e5' : (isDark ? '#374151' : '#e5e7eb') }}
                onClick={() => setIncludeUnclassified((v) => !v)}
              >
                <span
                  className="inline-block h-4 w-4 transform rounded-full bg-[var(--bg-raised)] shadow transition-transform duration-200"
                  style={{ transform: includeUnclassified ? 'translateX(18px)' : 'translateX(2px)' }}
                />
              </div>
              <span className="text-sm" style={{ color: headingText }}>
                Include unclassified
              </span>
              {unclassifiedCount > 0 && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-[var(--hover-tint)] text-[var(--text-ter)]">
                  {unclassifiedCount}
                </span>
              )}
            </label>
          </div>
        </div>

        {/* Custom weight inputs */}
        {activeScale === 'custom' && (
          <div className="pt-3 border-t" style={{ borderColor }}>
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: mutedText }}>
              Custom weights (units per item)
            </p>
            <div className="flex flex-wrap gap-3">
              {ALTITUDE_LEVELS_ORDERED.map((lvl) => (
                <div key={lvl} className="flex flex-col items-center gap-1.5">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ background: ALTITUDE_COLORS[lvl] }}
                  />
                  <label className="text-xs font-medium" style={{ color: mutedText }}>
                    L{lvl} {ALTITUDE_LEVEL_NAMES[lvl]}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    value={customWeights[lvl]}
                    onChange={(e) => {
                      const v = Math.max(1, parseInt(e.target.value) || 1);
                      setCustomWeights((prev) => ({ ...prev, [lvl]: v }));
                    }}
                    className="w-16 border px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-[var(--slate)]"
                    style={{
                      background: isDark ? 'var(--surface)' : '#fff',
                      borderColor: isDark ? 'var(--rule-bright)' : '#e5e7eb',
                      color: headingText,
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Main content: chart + table ────────────────────────────────────── */}
      {slices.length === 0 ? (
        <div
          className="border p-12 text-center"
          style={{ background: surfaceBg, borderColor }}
        >
          <Map className="h-10 w-10 mx-auto mb-3 opacity-25" style={{ color: mutedText }} />
          <p className="text-sm font-medium" style={{ color: mutedText }}>
            {selectedPool
              ? `No classified items found in "${selectedPool.name}".`
              : 'No classified items in the backlog yet.'}
          </p>
          <p className="text-xs mt-1" style={{ color: mutedText }}>
            {selectedPool
              ? 'Items in this pool need an altitude level assigned before they appear here.'
              : "Items with an altitude level will appear here once they've been classified."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chart panel */}
          <div
            className="border p-6 flex flex-col items-center justify-center"
            style={{ background: surfaceBg, borderColor }}
          >
            <div className="flex items-baseline gap-2 mb-4 self-start">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: mutedText }}>
                Effort Distribution
              </p>
              {selectedPool && (
                <span className="text-xs" style={{ color: mutedText }}>
                  — {selectedPool.name}
                </span>
              )}
            </div>
            <div className="relative w-full" style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={slices}
                    dataKey="totalUnits"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={82}
                    outerRadius={118}
                    paddingAngle={2}
                    startAngle={90}
                    endAngle={-270}
                    onMouseEnter={(_, idx) => setActiveSlice(idx)}
                    onMouseLeave={() => setActiveSlice(null)}
                  >
                    {slices.map((slice, i) => (
                      <Cell
                        key={`cell-${i}`}
                        fill={slice.color}
                        opacity={activeSlice !== null && activeSlice !== i ? 0.45 : 1}
                        strokeWidth={activeSlice === i ? 2 : 0}
                        stroke={activeSlice === i ? '#fff' : 'transparent'}
                        style={{ cursor: 'default', outline: 'none' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={
                      <SectorTooltip unitLabel={unitLabel} />
                    }
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Center label — rendered over the donut hole */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-3xl font-bold" style={{ color: headingText }}>
                  {totalUnits.toLocaleString()}
                </span>
                <span className="text-xs mt-0.5" style={{ color: mutedText }}>
                  total {unitLabel}s
                </span>
              </div>
            </div>

            {/* Legend dots */}
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 mt-3">
              {slices.map((slice) => (
                <div key={slice.level} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: slice.color }} />
                  <span className="text-xs" style={{ color: mutedText }}>
                    L{slice.level} {slice.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Breakdown table */}
          <div
            className="border overflow-hidden"
            style={{ background: surfaceBg, borderColor }}
          >
            <div className="px-5 pt-5 pb-3">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: mutedText }}>
                Effort Breakdown
              </p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-xs uppercase tracking-widest border-b"
                  style={{ borderColor, color: mutedText }}
                >
                  <th className="px-5 py-2 text-left font-medium">Altitude</th>
                  <th className="px-3 py-2 text-right font-medium">Items</th>
                  <th className="px-3 py-2 text-right font-medium">×Weight</th>
                  <th className="px-3 py-2 text-right font-medium">{unitLabel}s</th>
                  <th className="px-5 py-2 text-right font-medium">% Effort</th>
                </tr>
              </thead>
              <tbody>
                {slices.map((slice, i) => (
                  <tr
                    key={slice.level}
                    className="border-b transition-colors"
                    style={{
                      borderColor,
                      background:
                        activeSlice === i
                          ? isDark
                            ? 'rgba(255,255,255,0.04)'
                            : slice.color + '10'
                          : 'transparent',
                    }}
                    onMouseEnter={() => setActiveSlice(i)}
                    onMouseLeave={() => setActiveSlice(null)}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ background: slice.color }}
                        />
                        <div>
                          <div className="font-medium text-sm" style={{ color: headingText }}>
                            {slice.name}
                          </div>
                          <div className="text-xs mt-0.5" style={{ color: mutedText }}>
                            L{slice.level}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium" style={{ color: headingText }}>
                      {slice.count}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-xs font-medium" style={{ color: mutedText }}>
                      ×{slice.weight}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold" style={{ color: headingText }}>
                      {slice.totalUnits}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div
                          className="h-1.5 rounded-full"
                          style={{
                            width: `${Math.round(slice.pct)}px`,
                            maxWidth: 72,
                            minWidth: 4,
                            background: slice.color,
                            opacity: 0.7,
                          }}
                        />
                        <span
                          className="tabular-nums font-medium text-xs w-10 text-right"
                          style={{ color: headingText }}
                        >
                          {slice.pct.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb' }}>
                  <td className="px-5 py-3 text-xs font-semibold uppercase tracking-widest" style={{ color: mutedText }}>
                    Total
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums font-semibold text-sm" style={{ color: headingText }}>
                    {slices.reduce((s, r) => s + r.count, 0)}
                  </td>
                  <td className="px-3 py-3" />
                  <td className="px-3 py-3 text-right tabular-nums font-bold text-sm" style={{ color: headingText }}>
                    {totalUnits}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold text-xs" style={{ color: headingText }}>
                    100%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── Context note ───────────────────────────────────────────────────── */}
      <div
        className="border p-4 flex gap-3"
        style={{ background: isDark ? 'rgba(99,102,241,0.05)' : '#eef2ff', borderColor: isDark ? '#4f46e533' : '#c7d2fe' }}
      >
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-[var(--text-ter)]" />
        <div className="text-xs space-y-1" style={{ color: isDark ? '#a5b4fc' : '#4338ca' }}>
          <p className="font-semibold">How to read this chart</p>
          <p>
            Each sector's arc represents the <strong>weighted effort contribution</strong> of items at
            that altitude level — not the item count. A single L5 Strategic item with a
            ×{currentWeights[5]} weight contributes {currentWeights[5]}× more area than one
            L1 Tactical item. This makes the true shape of your backlog load visible.
          </p>
          <p className="mt-1">
            <strong>Weight scales</strong> match the measurement scale types in{' '}
            <a href="/prioritization/settings/value-scales" className="underline hover:opacity-80">
              Value Scales
            </a>
            . Fibonacci and Powers of 2 mirror the same sequences available on scoring attributes.
            Use Custom to set weights that match your team's specific estimation conventions.
          </p>
        </div>
      </div>
    </div>
  );
}
